import { Button, Card } from '@iwoca/orion';

import styles from './ANTCard.module.css';

import iwocaLogo from './iwocaLogoWhite.svg';
import { REQUEST_CHANGE_ROUTER } from '@iwRouter/IwRouter.actions';
import { Dispatch } from 'redux';
import { connect } from 'react-redux';
import {
    SanitisedFormalOffers,
    useApprovedNotTakenOffers
} from '@api/lendingAPI/lapiHooks';

const getSoonestTimeout = (offers: SanitisedFormalOffers) => {
    const offerWithSoonestTimeout = offers?.reduce((prev, curr) =>
        prev.timeout < curr.timeout ? prev : curr
    );
    return offerWithSoonestTimeout.timeout;
};

export const displayCurrency = (amount: number, decimalPlaces = 2) => {
    const currencyFormat = new Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency: 'GBP',
        minimumFractionDigits: decimalPlaces
    });
    return currencyFormat.format(amount);
};

const getFormattedDate = (
    dateString: string | null = '',
    format: 'short' | 'long' = 'short'
) => {
    const date = dateString ? new Date(dateString) : new Date();

    if (format === 'long') {
        const formatter = new Intl.DateTimeFormat('en-GB', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
        return formatter.format(date);
    }

    const formatter = new Intl.DateTimeFormat('en-GB', {
        year: '2-digit',
        month: 'short',
        day: 'numeric'
    });
    return formatter.format(date);
};

const mapDispatchToProps = (dispatch: Dispatch) => ({
    goToOffers: () =>
        dispatch({
            type: REQUEST_CHANGE_ROUTER,
            location: { pathname: '/my-offers/' }
        } as const)
});

type PropsFromRedux = ReturnType<typeof mapDispatchToProps>;

const ANTCard = ({ goToOffers }: PropsFromRedux) => {
    // Don't show ANT banner if ANT iwocaPay
    const { approvedNotTakenOffers } = useApprovedNotTakenOffers({
        excludedProducts: ['iwocapay']
    });

    if (!approvedNotTakenOffers) return null;

    if (approvedNotTakenOffers.length === 1) {
        const [offer] = approvedNotTakenOffers;
        const dateString = getFormattedDate(offer.timeout, 'long');

        return (
            <Card className={styles.cardContainer}>
                <div className={styles.innerContainer}>
                    <img src={iwocaLogo} className={styles.logo} alt="" />
                    <div>
                        <div className={styles.validUntil}>
                            Offer valid until {dateString}
                        </div>
                        <div className={styles.title}>
                            You're <b>approved</b> for a{' '}
                            <b>{displayCurrency(offer.available_cash, 0)}</b>{' '}
                            loan from iwoca.
                        </div>
                        <div>Hit the button to access these funds today.</div>
                        <Button
                            variant="secondary-alt"
                            onClick={goToOffers}
                            className={styles.button}
                        >
                            Access funds
                        </Button>
                    </div>
                </div>
            </Card>
        );
    }

    if (approvedNotTakenOffers.length > 1) {
        const timeout = getSoonestTimeout(approvedNotTakenOffers);
        const dateString = getFormattedDate(timeout, 'long');
        return (
            <Card className={styles.cardContainer}>
                <div className={styles.innerContainer}>
                    <img src={iwocaLogo} className={styles.logo} alt="" />
                    <div>
                        <div className={styles.validUntil}>
                            First offer valid until {dateString}
                        </div>
                        <div className={styles.title}>
                            You've been <b>approved</b> for more than one loan
                            with iwoca.
                        </div>
                        <div>
                            Hit the button to see your offers and choose the one
                            that works best for you.
                        </div>
                        <Button
                            variant="secondary-alt"
                            onClick={goToOffers}
                            className={styles.button}
                        >
                            See your offers
                        </Button>
                    </div>
                </div>
            </Card>
        );
    }
    return null;
};

// eslint-disable-next-line import/no-default-export
export default connect(null, mapDispatchToProps)(ANTCard);
